import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import ChildSnapshotCard from '@/components/ChildSnapshotCard';
import {
  Bell,
  User,
  ArrowRight,
  Clipboard,
  Clock,
  Flame,
  Sparkles,
  ChevronRight,
  Check,
  Droplet,
  Apple,
  Activity,
  Moon,
  Leaf,
  Footprints,
  Users,
  BookOpen,
  TrendingUp,
  HelpCircle,
  Loader2,
  Shield,
  Trophy,
  X,
  HeartHandshake,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import MilestoneModal from '@/components/MilestoneModal';
import StreakStory from '@/components/StreakStory';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import HealthAlertBanner from '@/components/HealthAlertBanner';
import BetaTestingBanner from '@/components/BetaTestingBanner';
import DailyHealthInsight from '@/components/DailyHealthInsight';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';
import { GlobalSearchTrigger } from '@/components/GlobalSearch';
import { useGlobalSearchContext } from '@/contexts/GlobalSearchContext';
import { WellnessPendingBadge } from '@/components/WellnessPendingBadge';
import deviceHealthService from '@/services/deviceHealthService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Settings, LogOut, HeartPulse, UsersRound } from 'lucide-react';
import { healthApi } from '@/api/healthApi';

// Tiered, score-aware copy for each tracked health factor.
//
// Localization (2026-05-24):
//   Previously this module held hardcoded English title/description pairs.
//   Now we resolve copy lazily via t() at render time from the
//   `dashboardDeep.rec.<factor>.<tier>.{title,description}` key tree.
//   The visible recommendation chooses the right tier based on:
//
//   - completed                   → 'maintenance' (no pressure, "keep it up" tone)
//   - score < 50  & not completed → 'priority'    (warm but firm, "do soon")
//   - score < 70  & not completed → 'improvement' (specific, encouraging)
//   - score ≥ 70  & not completed → 'maintenance' (low-stakes nudge)
const RECOMMENDATION_FACTORS = ['water', 'meals', 'steps', 'sleep'];
const RECOMMENDATION_TIERS = ['priority', 'improvement', 'maintenance'];

// Resolve a single recommendation copy block for the current locale.
// `t` is the translation function from useLanguage().
const getRecommendationCopy = (t, factor, tier) => {
  if (!RECOMMENDATION_FACTORS.includes(factor) || !RECOMMENDATION_TIERS.includes(tier)) {
    return null;
  }
  return {
    title: t(`dashboardDeep.rec.${factor}.${tier}.title`),
    description: t(`dashboardDeep.rec.${factor}.${tier}.description`),
  };
};

const TIER_PRIORITY = { priority: 0, improvement: 1, maintenance: 2 };

// Stable order used when several factors tie at the same tier — keeps
// the most-impactful upstream factor (water → sleep → meals → steps) on top.
const FACTOR_ORDER = ['water', 'sleep', 'meals', 'steps'];

// Streaks at which we show the celebration modal (matches backend STREAK_MILESTONES).
const MILESTONE_STREAKS = new Set([3, 7, 14, 21, 30, 60, 90, 180, 365]);

// Helpers for per-day notification dismissal persistence.
const todayKey = () => new Date().toISOString().split('T')[0];
const dismissedStorageKey = () => `hl_notif_dismissed_${todayKey()}`;
const loadDismissed = () => {
  try {
    const raw = localStorage.getItem(dismissedStorageKey());
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) {
    return new Set();
  }
};
const persistDismissed = (set) => {
  try {
    localStorage.setItem(dismissedStorageKey(), JSON.stringify(Array.from(set)));
  } catch (_) { /* ignore */ }
};

// Map backend task ids -> UI icons
const TASK_ICONS = {
  water: Droplet,
  meals: Apple,
  steps: Activity,
  sleep: Moon,
};

// Map insight category -> icon background color (soft, category-aware)
const INSIGHT_ICON = {
  sleep: Moon,
  hydration: Droplet,
  activity: Activity,
  nutrition: Apple,
  wellness: Sparkles,
  streak: Flame,
};

// Factor labels are locale-resolved at call time via t().
// Keys live under `dashboardDeep.factor.<id>` in the locales.
const getFactorLabel = (t, id) => t(`dashboardDeep.factor.${id}`);

// Locale-aware natural language join. Uses Intl.ListFormat where available
// (every modern browser + every modern locale we ship), with a quiet
// fallback to English if the runtime is missing it.
const localizedFactorList = (t, locale, factors) => {
  const labels = factors.map((f) => getFactorLabel(t, f));
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels);
  } catch (_) {
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
  }
};

// Curated wellness tips, grouped by the habit factor they reinforce.
// One tip is shown each day on the dashboard, picked by which habit the
// user most needs to nudge today (most-upstream incomplete: water → sleep
// → meals → steps). When everything is done we surface a maintenance/
// gratitude tip from the `general` pool. Tip text is keyed through i18n.
const TIPS_BY_FACTOR = {
  water: [
    { icon: '💧', textKey: 'tips.water1' },
    { icon: '💦', textKey: 'tips.water2' },
    { icon: '🍋', textKey: 'tips.water3' },
  ],
  meals: [
    { icon: '🥗', textKey: 'tips.meals1' },
    { icon: '🥑', textKey: 'tips.meals2' },
    { icon: '🍓', textKey: 'tips.meals3' },
  ],
  steps: [
    { icon: '🚶', textKey: 'tips.steps1' },
    { icon: '🌱', textKey: 'tips.steps2' },
    { icon: '🧗', textKey: 'tips.steps3' },
  ],
  sleep: [
    { icon: '🌙', textKey: 'tips.sleep1' },
    { icon: '😴', textKey: 'tips.sleep2' },
    { icon: '☀️', textKey: 'tips.sleep3' },
  ],
  general: [
    { icon: '🧘', textKey: 'tips.general1' },
    { icon: '💚', textKey: 'tips.general2' },
    { icon: '✨', textKey: 'tips.general3' },
  ],
};

// Pick a tip aligned to the user's most upstream pending habit. Falls back
// to a maintenance/gratitude tip when everything is done. Stable per day
// by default, but accepts a manual `cycleOffset` so the user can browse
// further tips within the same pool via the "Next tip" chevron.
const FACTOR_IDS = new Set(['water', 'meals', 'steps', 'sleep']);
const pickPersonalizedTip = (tasks, cycleOffset = 0) => {
  const incomplete = (tasks || [])
    .filter((t) => !t.completed)
    .map((t) => t.id)
    .filter((id) => FACTOR_IDS.has(id))
    .sort((a, b) => FACTOR_ORDER.indexOf(a) - FACTOR_ORDER.indexOf(b));

  const factor = incomplete[0];
  const pool = (factor && TIPS_BY_FACTOR[factor]) || TIPS_BY_FACTOR.general;
  const category = factor || 'maintenance';

  const dayIndex = Math.floor(Date.now() / 86400000);
  const idx = (dayIndex + cycleOffset) % pool.length;
  return { ...pool[idx], category, poolSize: pool.length };
};

// Pick a personalised insight sentence from the user's current incomplete
// factors. The variants live in the locales under
// `dashboardDeep.insight.pair.<a>+<b>` as a comma-joined string (because
// the t() helper returns strings only — we split into an array client-side).
// Day-to-day rotation comes from the date seed so the message doesn't
// repeat across days even when the same factors are pending.
const INSIGHT_PAIR_KEYS = [
  'water+sleep', 'water+meals', 'water+steps',
  'meals+sleep', 'meals+steps', 'steps+sleep',
];

// Pull pair variants out of the i18n tree (an array per locale).
// Falls back to an English string when the locale key is missing.
const getInsightPairVariants = (t, pairKey) => {
  // Use t() with a sentinel fallback to detect a missing array key.
  // We store variants as separate keys: .v1, .v2 (avoids storing arrays in
  // the locale JSON, which the current t() helper doesn't traverse).
  const v1 = t(`dashboardDeep.insight.pair.${pairKey}.v1`);
  const v2 = t(`dashboardDeep.insight.pair.${pairKey}.v2`);
  const out = [];
  if (v1 && !v1.startsWith('dashboardDeep.')) out.push(v1);
  if (v2 && !v2.startsWith('dashboardDeep.')) out.push(v2);
  return out;
};

const buildHealthInsight = (t, locale, tasks, score) => {
  const incomplete = (tasks || [])
    .filter((task) => !task.completed)
    .map((task) => task.id)
    .filter((id) => FACTOR_IDS.has(id))
    .sort((a, b) => FACTOR_ORDER.indexOf(a) - FACTOR_ORDER.indexOf(b));

  // All four done — celebrate, never repeat.
  if (incomplete.length === 0) {
    return t('dashboardDeep.insight.allOnTrack');
  }

  // Single factor pending.
  if (incomplete.length === 1) {
    const label = getFactorLabel(t, incomplete[0]);
    return t('dashboardDeep.insight.singlePending', { factor: label });
  }

  // Two or more pending — try the curated pair library first.
  const pair = incomplete.slice(0, 2);
  const key = pair[0] && pair[1] && INSIGHT_PAIR_KEYS.includes(`${pair[0]}+${pair[1]}`)
    ? `${pair[0]}+${pair[1]}`
    : null;
  if (key) {
    const variants = getInsightPairVariants(t, key);
    if (variants.length) {
      const seed = Number(new Date().toISOString().slice(0, 10).replace(/-/g, ''));
      return variants[seed % variants.length];
    }
  }

  // Generic two-factor fallback (Intl-joined, locale-aware).
  const factors = localizedFactorList(t, locale, pair);
  if (score < 50) {
    return t('dashboardDeep.insight.pairLowScore', { factors });
  }
  return t('dashboardDeep.insight.pairOk', { factors });
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tracker, setTracker] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [insights, setInsights] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [streakStatus, setStreakStatus] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestDismissed, setDigestDismissed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [togglingTaskId, setTogglingTaskId] = useState(null);
  // Tiny mount-flag that toggles to true ~80ms after first paint so the
  // health-score ring animates from 0 → target on every dashboard load.
  const [ringDrawn, setRingDrawn] = useState(false);
  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => setRingDrawn(true), 80);
    return () => clearTimeout(t);
  }, [loading]);

  // Beta-feedback engagement tracking — record that the user visited
  // the dashboard so the Smart Feedback Prompt's "≥3 unique sections"
  // trigger can fire. $addToSet on the server makes this idempotent.
  const { trackSectionVisit: _trackSectionVisit } = useBetaFeedback();
  useEffect(() => {
    _trackSectionVisit('dashboard');
    // 1-minute session-time ping while the tab is focused — feeds the
    // ≥5-minute eligibility trigger. Capped server-side so a forgotten
    // tab can't inflate the counter.
    const SECONDS_PER_PING = 60;
    let pingTimer = null;
    const pingNow = () => {
      try {
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/feedback/session-ping`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ seconds: SECONDS_PER_PING }),
            keepalive: true,
          },
        ).catch(() => {});
      } catch { /* silent */ }
    };
    const start = () => {
      if (pingTimer) return;
      pingTimer = setInterval(pingNow, SECONDS_PER_PING * 1000);
    };
    const stop = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    };
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') start();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wellness tip interactivity: cycle index within the current pool,
  // and an "acknowledged" flag the user can toggle with a Got it pill.
  const [tipCycleIdx, setTipCycleIdx] = useState(0);
  const [tipAcked, setTipAcked] = useState(false);
  const recordTipEngagement = useCallback((action, category, tipText) => {
    // Fire-and-forget telemetry — never blocks the UI, never surfaces errors.
    healthApi.recordTipEngagement(action, category, tipText).catch(() => {});
  }, []);
  const [dismissedNotifs, setDismissedNotifs] = useState(() => loadDismissed());
  const [milestoneStreak, setMilestoneStreak] = useState(null);

  // Global Search — Cmd/Ctrl+K opens it from anywhere on the dashboard.
  // Trigger surfaces: header icon (top-right) + hero pill (below header).
  // The modal + ⌘K listener is owned by <GlobalSearchProvider> at the App
  // root so it stays a single shared instance regardless of viewport.
  const { openSearch } = useGlobalSearchContext();

  /**
   * Quick Access sub-line signals — strictly read-only, sourced from data
   * we already have on Home or in the device's local cache. No network
   * calls, no polling: we read once on mount and again whenever the tab
   * regains focus (cheap localStorage read). Keeping signals optional and
   * conditional so tiles without a meaningful number stay clean.
   */
  const [cachedSteps, setCachedSteps] = useState(() => {
    try { return deviceHealthService.getCachedSteps(); } catch (_) { return 0; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setCachedSteps(deviceHealthService.getCachedSteps()); } catch (_) { /* cache unavailable */ }
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const [trackerRes, insightsRes, notifsRes, streakRes, digestRes] = await Promise.all([
        healthApi.getDailyTracker(),
        healthApi.getAIInsights().catch(() => ({ data: { insights: [] } })),
        healthApi.getComputedNotifications().catch(() => ({ data: { notifications: [] } })),
        healthApi.getStreakStatus().catch(() => ({ data: null })),
        healthApi.getWeeklyDigest().catch(() => ({ data: null })),
      ]);
      setTracker(trackerRes.data.tracker);
      setTasks(trackerRes.data.tasks || []);
      setInsights(insightsRes.data?.insights || []);
      setNotifications(notifsRes.data?.notifications || []);
      setStreakStatus(streakRes.data || null);
      setDigest(digestRes.data || null);

      // Respect a one-off dismissal per week
      try {
        const weekOf = digestRes.data?.week_of;
        if (weekOf && localStorage.getItem(`hl_digest_dismissed_${weekOf}`)) {
          setDigestDismissed(true);
        }
      } catch (_) { /* ignore */ }
    } catch (err) {
      if (navigator.onLine) {
        toast.error(t('dashboardDeep.dashLoadError'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Once-only milestone reveal — keyed by user id + streak so each celebration
  // fires exactly once even across reloads.
  const tryShowMilestone = useCallback(
    (streak) => {
      if (!streak || !MILESTONE_STREAKS.has(streak)) return;
      try {
        const key = `hl_milestone_${user?.id || 'anon'}_${streak}`;
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
      } catch (_) { /* ignore */ }
      setMilestoneStreak(streak);
    },
    [user?.id]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Show milestone modal once per user/streak whenever we see one of the
  // celebrated streak counts on the dashboard tracker.
  useEffect(() => {
    if (!loading && tracker?.current_streak) {
      tryShowMilestone(tracker.current_streak);
    }
  }, [loading, tracker?.current_streak, tryShowMilestone]);

  // Sync user's browser timezone once per session so nudges run at true local time.
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && !sessionStorage.getItem('hl_tz_synced')) {
        sessionStorage.setItem('hl_tz_synced', tz);
        healthApi.updateTimezone(tz).catch(() => { /* silent */ });
      }
    } catch (_) { /* ignore */ }
  }, []);

  // If the user arrives back on the dashboard already at 4/4 today (e.g. after submitting the
  // Smart Check-in), fire the celebration exactly once per day.
  useEffect(() => {
    if (!loading && tracker?.completion_percentage === 100) {
      celebratePerfectDay(tracker.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tracker?.date, tracker?.completion_percentage]);

  const dismissNotification = (id) => {
    setDismissedNotifs((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissed(next);
      return next;
    });
  };

  const handleNotificationClick = (n) => {
    dismissNotification(n.id);
    setShowNotifications(false);
    navigate(n.action_route || '/smart-check-in');
  };

  const handleToggleTask = async (taskId) => {
    if (togglingTaskId) return;
    setTogglingTaskId(taskId);

    // Optimistic update
    const prevTasks = tasks;
    const prevTracker = tracker;
    const wasComplete = prevTasks.every((t) => t.completed);
    const optimisticTasks = tasks.map((t) =>
      t.id === taskId ? { ...t, completed: !t.completed } : t
    );
    setTasks(optimisticTasks);

    try {
      const { data } = await healthApi.toggleDailyTask(taskId);
      setTracker(data.tracker);
      setTasks(data.tasks || optimisticTasks);

      // Completion reward — only when user transitions to a full 4/4 for the first time today
      const justCompleted =
        !wasComplete && data.tracker?.completion_percentage === 100;
      if (justCompleted) {
        celebratePerfectDay(data.tracker?.date);
      }

      if (data.streak_milestone) {
        // Backend echoed a milestone string — fire the celebration modal once
        // per (user, streak) and skip the toast (modal carries the message).
        tryShowMilestone(data.tracker?.current_streak);
      } else if (
        data.motivational_message &&
        data.tracker?.completion_percentage === 100 &&
        !justCompleted
      ) {
        toast.success(data.motivational_message);
      }
    } catch (err) {
      setTasks(prevTasks);
      setTracker(prevTracker);
      toast.error(t('dashboardDeep.taskUpdateError'));
    } finally {
      setTogglingTaskId(null);
    }
  };

  const celebratePerfectDay = (date) => {
    // Once-per-day guard
    try {
      const key = `hl_celebrated_${date || new Date().toISOString().split('T')[0]}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch (_) { /* ignore */ }

    // Subtle, fast burst (< 1 second)
    try {
      const end = Date.now() + 700;
      const colors = ['#10b981', '#14b8a6', '#a855f7', '#f59e0b'];
      (function frame() {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors,
          scalar: 0.7,
          gravity: 1.2,
          ticks: 60,
          disableForReducedMotion: true,
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors,
          scalar: 0.7,
          gravity: 1.2,
          ticks: 60,
          disableForReducedMotion: true,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    } catch (_) { /* ignore */ }

    // Light haptic (mobile only, no-op where unsupported)
    try {
      if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
    } catch (_) { /* ignore */ }

    toast.success(t('dashboardDeep.perfectDay'));
  };

  const dismissDigest = () => {
    try {
      if (digest?.week_of) {
        localStorage.setItem(`hl_digest_dismissed_${digest.week_of}`, '1');
      }
    } catch (_) { /* ignore */ }
    setDigestDismissed(true);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  const getGreetingMeta = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { emoji: '🌞', label: t('dashboard.goodMorning') };
    if (hour < 18) return { emoji: '☀️', label: t('dashboard.goodAfternoon') };
    return { emoji: '🌙', label: t('dashboard.goodEvening') };
  };

  // Subtle, warm, time-aware welcome copy. Rotated daily so the same hour
  // doesn't always show the same line — gentle variety, never noisy.
  const getWelcomeSubtitle = () => {
    const hour = new Date().getHours();
    const dayOfYear = (() => {
      const start = new Date(new Date().getFullYear(), 0, 0);
      return Math.floor((Date.now() - start.getTime()) / 86400000);
    })();
    const morning = [
      t('dashboard.welcomeSubtitleMorning1'),
      t('dashboard.welcomeSubtitleMorning2'),
      t('dashboard.welcomeSubtitleMorning3'),
    ];
    const afternoon = [
      t('dashboard.welcomeSubtitleAfternoon1'),
      t('dashboard.welcomeSubtitleAfternoon2'),
      t('dashboard.welcomeSubtitleAfternoon3'),
    ];
    const evening = [
      t('dashboard.welcomeSubtitleEvening1'),
      t('dashboard.welcomeSubtitleEvening2'),
      t('dashboard.welcomeSubtitleEvening3'),
    ];
    const bucket = hour < 12 ? morning : hour < 18 ? afternoon : evening;
    return bucket[dayOfYear % bucket.length];
  };

  const getInitials = () => {
    const raw = (user?.name || '').trim();
    if (!raw) return user?.email?.[0]?.toUpperCase() || '?';
    const parts = raw.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  };

  const handleLogout = async () => {
    try { await logout(); } catch (_) { /* still navigate even if API fails */ }
    navigate('/login');
  };

  const getUserName = () => user?.name?.split(' ')[0] || 'there';

  const healthScore = tracker?.health_score ?? 0;
  const currentStreak = tracker?.current_streak ?? 0;
  const todaysProgress = tracker?.completion_percentage ?? 0;
  const tasksCompleted = tasks.filter((t) => t.completed).length;
  const totalTasks = tasks.length || 4;

  // Mixed-language guard (2026-05-24): backend-generated insights and
  // computed notifications are still English-only. Until backend i18n
  // ships, hide them from non-English locales so the dashboard never
  // shows mixed-language content. The fully-localized recommendation
  // grid + buildHealthInsight summary remain visible in every locale.
  const showServerCopy = currentLanguage === 'en';
  const primaryInsight = showServerCopy ? insights[0] : null;
  const InsightIcon = primaryInsight ? INSIGHT_ICON[primaryInsight.category] || Sparkles : Sparkles;
  const visibleNotifications = showServerCopy
    ? notifications.filter((n) => !dismissedNotifs.has(n.id))
    : [];
  const unseenCount = visibleNotifications.length;

  const scoreLabel =
    healthScore >= 80 ? 'Great' : healthScore >= 60 ? 'Good' : healthScore >= 40 ? 'Fair' : 'Start today';

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 overflow-y-auto pb-32"
      data-testid="dashboard-page"
    >
      <div className="max-w-xl mx-auto md:max-w-3xl lg:max-w-5xl xl:max-w-6xl md:px-4 lg:px-6">

        {/* Email verification banner — soft, dismissible. Renders only when
            the logged-in user has email_verified === false. */}
        <EmailVerificationBanner />

        {/* Beta testing banner — between email-verification + health-alert
            per launch plan. Server-side 14-day dismissal cooldown. */}
        <div className="px-4 sm:px-6 pt-3" data-testid="dashboard-beta-banner-slot">
          <BetaTestingBanner />
        </div>

        {/* Global health alert (WHO / NHS) — dismissible per device, single
            alert at a time. Hidden if none is published. */}
        <div className="px-4 sm:px-6 pt-3" data-testid="dashboard-health-alert-slot">
          <HealthAlertBanner />
        </div>

        {/* 1. HEADER — mobile-only. Desktop users see <DesktopHeader />
            mounted globally in App.js which provides the full nav + same
            search, notifications and profile dropdown surfaces. */}
        <div
          className="bg-white/80 backdrop-blur-sm border-b border-gray-100 px-6 py-4 sticky top-0 z-20 shadow-sm md:hidden"
          data-testid="dashboard-header"
        >
          <div className="flex items-center justify-between">
            <HealthLoopLogo size="sm" showText={true} />
            <div className="flex items-center gap-2 sm:gap-3">
              <GlobalSearchTrigger variant="icon" onClick={openSearch} />
              <button
                className="relative"
                onClick={() => setShowNotifications((v) => !v)}
                data-testid="dashboard-notifications-btn"
                aria-label={t('dashboardDeep.notificationsAria')}
              >
                <Bell className="h-6 w-6 text-gray-700" />
                {unseenCount > 0 && !loading && (
                  <span
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold"
                    data-testid="dashboard-notifications-badge"
                  >
                    {unseenCount}
                  </span>
                )}
              </button>

              {/* Avatar with initials + active dot + click animation, opens menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="relative outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 rounded-full transition-transform duration-150 active:scale-90 hover:scale-105"
                    aria-label={t('dashboardDeep.profileMenuAria')}
                    data-testid="dashboard-profile-btn"
                  >
                    <span
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-600 text-white text-sm font-bold shadow-md ring-2 ring-white"
                      data-testid="dashboard-avatar-initials"
                    >
                      {getInitials()}
                    </span>
                    {/* Streak flame badge — appears once the user has a 3+ day streak.
                        Sits on the opposite corner from the active dot so they never overlap. */}
                    {currentStreak >= 3 && (
                      <span
                        className="absolute -top-1 -right-1 flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 text-white text-[10px] font-bold shadow ring-2 ring-white"
                        data-testid="dashboard-avatar-streak-badge"
                        data-streak={currentStreak}
                        aria-label={`${currentStreak}-day streak`}
                        title={`${currentStreak}-day streak`}
                      >
                        <Flame className="h-2.5 w-2.5" aria-hidden="true" />
                        <span className="leading-none">{currentStreak}</span>
                      </span>
                    )}
                    {/* Active indicator — color reflects email verification status.
                        Emerald (verified) reinforces the secure-account state;
                        amber (unverified) visually echoes the verification banner. */}
                    {(() => {
                      const verified = !!user?.email_verified;
                      const dotColor = verified ? 'bg-emerald-500' : 'bg-amber-400';
                      const label = verified ? 'Account verified' : 'Email not verified';
                      return (
                        <span
                          className={`absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white ${dotColor}`}
                          data-testid="dashboard-avatar-active-dot"
                          data-verified={verified ? 'true' : 'false'}
                          aria-label={label}
                          title={label}
                        >
                          {verified && (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 rounded-full bg-emerald-400 opacity-70 animate-ping"
                            />
                          )}
                        </span>
                      );
                    })()}
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  sideOffset={10}
                  className="w-64 rounded-xl shadow-lg border-gray-100"
                  data-testid="dashboard-profile-menu"
                >
                  <DropdownMenuLabel className="px-3 py-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span
                        className="text-sm font-semibold text-gray-900 truncate"
                        data-testid="profile-menu-name"
                      >
                        {user?.name || t('profileDeep.healthLoopUser')}
                      </span>
                      <span
                        className="text-xs font-normal text-gray-500 truncate"
                        data-testid="profile-menu-email"
                      >
                        {user?.email || ''}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    className="gap-2 cursor-pointer"
                    data-testid="profile-menu-health-profile"
                  >
                    <HeartPulse className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.healthProfile')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/notifications')}
                    className="gap-2 cursor-pointer"
                    data-testid="profile-menu-notifications"
                  >
                    <Bell className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.notifications')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/family-health')}
                    className="gap-2 cursor-pointer"
                    data-testid="profile-menu-family"
                  >
                    <UsersRound className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.familyAccounts')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/settings/reminders')}
                    className="gap-2 cursor-pointer"
                    data-testid="profile-menu-settings"
                  >
                    <Settings className="h-4 w-4 text-emerald-600" />
                    <span>{t('dashboardDeep.settings')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="gap-2 cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                    data-testid="profile-menu-logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t('dashboardDeep.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6 space-y-7">

          {/* Global Search — prominent entry into the app's universal
              navigation. Lightweight, frontend-only; opens the same modal as
              the header icon and the ⌘K shortcut. */}
          <GlobalSearchTrigger variant="pill" onClick={openSearch} />

          {/* Wellness sync chip — appears only when offline updates are
              waiting across check-ins / reminders / step tracker. Hides
              itself when everything is in sync, keeping Home calm. */}
          <div className="flex justify-center">
            <WellnessPendingBadge variant="chip" data-testid="dashboard-wellness-pending" />
          </div>

          {/* Notifications panel (inline, collapsible, dismissible) */}
          {showNotifications && visibleNotifications.length > 0 && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="dashboard-notifications-panel">
              {visibleNotifications.map((n) => {
                const bg =
                  n.priority === 'high'
                    ? 'border-l-red-500 bg-red-50'
                    : 'border-l-amber-500 bg-amber-50';
                return (
                  <div
                    key={n.id}
                    className={`relative border-l-4 ${bg} rounded-r-xl shadow-sm hover:shadow-md transition-all`}
                    data-testid={`notification-${n.type}`}
                  >
                    <button
                      onClick={() => handleNotificationClick(n)}
                      className="w-full text-left p-4 pr-10"
                    >
                      <p className="text-sm font-bold text-gray-900 mb-1">{n.title}</p>
                      <p className="text-xs text-gray-700 mb-2">{n.message}</p>
                      <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                        {n.action_label}
                        <ChevronRight className="h-3 w-3" />
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotification(n.id);
                      }}
                      aria-label={t('dashboardDeep.dismissNotification')}
                      className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-white/60 transition-colors"
                      data-testid={`notification-dismiss-${n.type}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {showNotifications && visibleNotifications.length === 0 && (
            <div
              className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-center"
              data-testid="dashboard-notifications-empty"
            >
              <p className="text-sm font-semibold text-emerald-900">{t('dashboardDeep.allCaughtUp')}</p>
              <p className="text-xs text-emerald-800/80 mt-1">No alerts right now — enjoy the calm.</p>
            </div>
          )}

          {/* Weekly Digest — shown on Mondays, dismissible */}
          {digest?.show && !digestDismissed && !loading && (
            <Card
              className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50 relative overflow-hidden"
              data-testid="dashboard-weekly-digest"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Trophy className="h-4 w-4 text-amber-600" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{t('dashboardDeep.weekInReview')}</h3>
                  </div>
                  <button
                    onClick={dismissDigest}
                    aria-label={t('dashboardDeep.dismissWeeklyDigest')}
                    className="text-gray-400 hover:text-gray-600"
                    data-testid="digest-dismiss-btn"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {digest.total_checkins}
                    </p>
                    <p className="text-xs text-gray-600">check-ins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {digest.perfect_days}
                    </p>
                    <p className="text-xs text-gray-600">perfect days</p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`text-2xl font-bold ${
                        digest.trend === 'improving'
                          ? 'text-emerald-600'
                          : digest.trend === 'declining'
                          ? 'text-amber-700'
                          : 'text-gray-900'
                      }`}
                    >
                      {digest.trend === 'improving' ? '↑' : digest.trend === 'declining' ? '↓' : '→'}
                    </p>
                    <p className="text-xs text-gray-600 capitalize">{digest.trend}</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {digest.biggest_win}
                </p>
                <p className="text-xs text-gray-700">{digest.trend_message}</p>
              </CardContent>
            </Card>
          )}

          {/* Personalized welcome banner — soft wellness gradient, premium mobile-first card */}
          <div
            className="relative overflow-hidden rounded-3xl p-6 sm:p-7 shadow-md ring-1 ring-emerald-100/60 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 hl-fade-up"
            data-testid="dashboard-welcome-banner"
          >
            {/* soft glows */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-emerald-200/40 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-teal-200/40 blur-3xl"
            />
            <div className="relative">
              <h1
                className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight"
                data-testid="dashboard-title"
              >
                <span className="mr-2" aria-hidden="true">{getGreetingMeta().emoji}</span>
                {getGreetingMeta().label},{' '}
                <span className="bg-gradient-to-r from-emerald-700 to-teal-600 bg-clip-text text-transparent">
                  {getUserName()}
                </span>
              </h1>
              <p
                className="mt-2 text-sm sm:text-base text-gray-700"
                data-testid="dashboard-welcome-back"
              >
                {getWelcomeSubtitle()}
              </p>
              <p
                className="mt-3 text-xs sm:text-sm text-emerald-800/80 italic"
                data-testid="dashboard-tagline"
              >
                {t('dashboard.tagline')} 💚
              </p>
              <p
                className="mt-1 text-[11px] text-emerald-700/70"
                data-testid="dashboard-microcopy"
              >
                {t('dashboard.microcopy')}
              </p>
            </div>
          </div>

          {/* Daily wellness insight — personalised micro-tip from the
              backend. Replaces the duplicate "Help improve HealthLoop"
              feedback card (the top BetaTestingBanner remains the single
              feedback entry point on the home dashboard). */}
          <div className="mt-5 sm:mt-6" data-testid="dashboard-daily-insight-slot">
            <DailyHealthInsight embedded />
          </div>

          {loading ? (
            <div
              className="space-y-4"
              data-testid="dashboard-loading"
              role="status"
              aria-live="polite"
              aria-label={t('dashboardDeep.loadingWellness')}
            >
              <div className="hl-shimmer rounded-3xl h-32 ring-1 ring-emerald-100/60" />
              <div className="grid grid-cols-2 gap-3">
                <div className="hl-shimmer rounded-2xl h-24" />
                <div className="hl-shimmer rounded-2xl h-24" />
              </div>
              <div className="hl-shimmer rounded-3xl h-40" />
              <span className="sr-only">Loading your wellness summary…</span>
            </div>
          ) : (
            <>
              {/* PROMINENT HEALTH SCORE CARD — top priority */}
              {(() => {
                const score = healthScore;
                // Empty-state trigger: a brand-new user with no completed
                // habits, no streak, and a 0 score gets a friendly onboarding
                // card instead of an "0/100 Needs Attention" framing.
                const isOnboarding = score === 0 && currentStreak === 0 && tasksCompleted === 0;

                let status, statusEmoji, accent, ring, ringTrack, supporting, band;
                if (isOnboarding) {
                  status = t('dashboardDeep.scoreReadyWhenYou');
                  statusEmoji = '💚';
                  band = 'onboarding';
                  accent = 'from-emerald-50 to-teal-50 border-emerald-100';
                  ring = '#10b981';
                  ringTrack = '#d1fae5';
                  supporting = t('dashboardDeep.scoreCompleteFirstCheck');
                } else if (score >= 85) {
                  status = t('dashboardDeep.scoreExcellent');
                  statusEmoji = '✨';
                  band = 'excellent';
                  accent = 'from-emerald-50 to-teal-50 border-emerald-100';
                  ring = '#10b981';
                  ringTrack = '#d1fae5';
                  supporting = t('dashboardDeep.scoreSupportExcellent');
                } else if (score >= 70) {
                  status = t('dashboardDeep.scoreGood');
                  statusEmoji = '🙂';
                  band = 'good';
                  accent = 'from-emerald-50 to-lime-50 border-emerald-100';
                  ring = '#10b981';
                  ringTrack = '#d1fae5';
                  supporting = t('dashboardDeep.scoreSupportGood');
                } else if (score >= 50) {
                  status = t('dashboardDeep.scoreModerate');
                  statusEmoji = '⚠️';
                  band = 'moderate';
                  accent = 'from-amber-50 to-yellow-50 border-amber-100';
                  ring = '#f59e0b';
                  ringTrack = '#fef3c7';
                  supporting = t('dashboardDeep.scoreSupportModerate');
                } else {
                  status = t('dashboardDeep.scoreNeedsAttention');
                  statusEmoji = '💡';
                  band = 'needs_attention';
                  accent = 'from-orange-50 to-amber-50 border-orange-100';
                  ring = '#f97316';
                  ringTrack = '#fed7aa';
                  supporting = t('dashboardDeep.scoreSupportNeedsAttention');
                }
                // For the empty state, fill the ring 25% as a friendly hint
                // ("you have potential") rather than leave it stark empty.
                const targetPct = isOnboarding ? 25 : Math.max(0, Math.min(100, score));
                const dash = (targetPct / 100) * 264;
                return (
                  <>
                    <Card
                      className={`border bg-gradient-to-br ${accent} shadow-md rounded-2xl overflow-hidden`}
                      data-testid="dashboard-health-score-hero"
                      data-band={band}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-5">
                          <div className="relative w-24 h-24 shrink-0">
                            <svg className="transform -rotate-90 w-24 h-24" viewBox="0 0 96 96">
                              <circle cx="48" cy="48" r="42" stroke={ringTrack} strokeWidth="8" fill="transparent" />
                              <circle
                                cx="48" cy="48" r="42"
                                stroke={ring}
                                strokeWidth="8"
                                fill="transparent"
                                strokeDasharray={`${ringDrawn ? dash : 0} 264`}
                                strokeLinecap="round"
                                style={{ transition: 'stroke-dasharray 1100ms cubic-bezier(0.16, 1, 0.3, 1)' }}
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              {isOnboarding ? (
                                <>
                                  <span
                                    className="text-3xl leading-none"
                                    aria-hidden="true"
                                  >💚</span>
                                  <span className="text-[10px] text-emerald-700 font-medium mt-1">
                                    {t('dashboardDeep.letsStart')}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span
                                    className="text-4xl font-bold text-gray-900 leading-none tabular-nums"
                                    data-testid="health-score-hero-value"
                                  >
                                    {score}
                                  </span>
                                  <span className="text-[10px] text-gray-500 mt-1">/ 100</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                              {isOnboarding ? t('dashboardDeep.healthScoreNew') : t('dashboardDeep.healthScoreLabel')}
                            </p>
                            <p
                              className="text-lg font-bold text-gray-900 leading-tight"
                              data-testid="health-score-hero-status"
                            >
                              {isOnboarding ? (
                                <>{t('dashboardDeep.letsBuildScore')} {statusEmoji}</>
                              ) : (
                                <>{status} {statusEmoji}</>
                              )}
                            </p>
                            <p className="text-sm text-gray-700 leading-snug mt-1.5">
                              {supporting}
                            </p>
                            {isOnboarding && (
                              <p className="text-xs text-emerald-700 mt-3 italic">
                                {t('dashboardDeep.oneHealthyHabitAtATime')}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Next check pill — separated so the score card itself stays uncluttered */}
                    <p
                      className="-mt-3 text-xs text-gray-500 flex items-center gap-1.5 px-1"
                      data-testid="dashboard-next-check"
                    >
                      <Clock className="h-3.5 w-3.5" />
                      <span>
                        {t('dashboardDeep.nextCheck')} <span className="text-gray-700 font-medium">{t('dashboardDeep.todayAt8pm')}</span>
                      </span>
                    </p>
                  </>
                );
              })()}

              {/* TODAY'S WELLNESS TIP — personalised by which habit needs
                  the most attention today; user can browse further tips in
                  the same pool via the Next chevron and ack with Got it. */}
              {(() => {
                const tip = pickPersonalizedTip(tasks, tipCycleIdx);
                const tipText = t(tip.textKey);
                const eyebrow = tip.category === 'maintenance'
                  ? t('dashboardDeep.wellnessTipForYou')
                  : t('dashboardDeep.wellnessTipForFactor', { factor: getFactorLabel(t, tip.category) });
                const onNext = () => {
                  setTipAcked(false);
                  const nextIdx = tipCycleIdx + 1;
                  setTipCycleIdx(nextIdx);
                  recordTipEngagement('next', tip.category, tipText);
                };
                const onAck = () => {
                  setTipAcked(true);
                  recordTipEngagement('got_it', tip.category, tipText);
                };
                const canCycle = tip.poolSize > 1;
                return (
                  <Card
                    className="border border-emerald-100/70 bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/40 shadow-sm rounded-2xl overflow-hidden"
                    data-testid="dashboard-wellness-tip"
                    data-tip-category={tip.category}
                  >
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex items-start gap-4">
                        <div
                          className="shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 text-2xl shadow-inner"
                          aria-hidden="true"
                        >
                          <span className="animate-pulse">{tip.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"
                            data-testid="wellness-tip-eyebrow"
                          >
                            <Sparkles className="h-3 w-3" aria-hidden="true" />
                            {eyebrow}
                          </p>
                          <p
                            className="text-sm sm:text-[15px] text-gray-800 leading-relaxed"
                            data-testid="wellness-tip-text"
                          >
                            {tipText}
                          </p>

                          {/* Interactive footer: Got it pill + Next chevron */}
                          <div className="mt-4 flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={onAck}
                              disabled={tipAcked}
                              data-testid="wellness-tip-ack-btn"
                              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium transition-colors ${
                                tipAcked
                                  ? 'bg-emerald-600 text-white cursor-default'
                                  : 'bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 active:scale-95'
                              }`}
                            >
                              {tipAcked ? (
                                <>
                                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                  {t('dashboardDeep.logged')}
                                </>
                              ) : (
                                <>{t('dashboardDeep.gotIt')}</>
                              )}
                            </button>

                            {canCycle && (
                              <button
                                type="button"
                                onClick={onNext}
                                data-testid="wellness-tip-next-btn"
                                className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium text-emerald-700 hover:bg-emerald-50 active:scale-95 transition-colors"
                                aria-label={t('dashboardDeep.nextTip')}
                              >
                                {t('dashboardDeep.nextTip')}
                                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                              </button>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 italic mt-3">
                            {t('dashboardDeep.gladToSeeYou')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* 2. PRIMARY ACTION CARD (HERO) */}
              <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-teal-600 overflow-hidden relative">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-emerald-100 uppercase tracking-wider mb-2">
                        {t('dashboardDeep.yourNextStepToday')}
                      </p>
                      <h2 className="text-xl font-bold text-white mb-1">
                        {t('dashboardDeep.completeDailyCheck')}
                      </h2>
                      <p className="text-sm text-emerald-50 mb-5">
                        {t('dashboardDeep.takesLessThan60Sec')}
                      </p>
                      <Button
                        onClick={() => navigate('/smart-check-in')}
                        className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold h-12 px-6 rounded-xl shadow-lg"
                        data-testid="dashboard-start-daily-check-btn"
                      >
                        {t('dashboardDeep.startDailyCheck')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                    <div className="ml-4">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                        <Clipboard className="h-10 w-10 text-white" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Calm narrative — replaces the harsh streak number as the headline */}
              <StreakStory />

              {/* 3. TODAY AT A GLANCE */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{t('dashboardDeep.todayAtGlance')}</h3>
                  <button
                    className="text-sm text-emerald-600 font-medium"
                    onClick={() => navigate('/preventive-health')}
                    data-testid="dashboard-view-all-metrics-btn"
                  >
                    {t('dashboardDeep.viewAll')}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">

                  {/* Health Score */}
                  <Card className="border-0 shadow-md" data-testid="dashboard-health-score-card">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-600 mb-2">{t('dashboardDeep.healthScoreLabel')}</p>
                      <div className="relative w-16 h-16 mx-auto mb-2">
                        <svg className="transform -rotate-90 w-16 h-16">
                          <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="6" fill="transparent" />
                          <circle
                            cx="32" cy="32" r="28"
                            stroke="#10b981"
                            strokeWidth="6"
                            fill="transparent"
                            strokeDasharray={`${(healthScore / 100) * 176} 176`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold text-gray-900" data-testid="health-score-value">
                            {healthScore}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">/100</span>
                      <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center justify-center gap-1">
                        {scoreLabel} <TrendingUp className="h-3 w-3" />
                      </p>
                    </CardContent>
                  </Card>

                  {/* Current Streak */}
                  <Card className="border-0 shadow-md relative" data-testid="dashboard-streak-card">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-600 mb-2">{t('dashboardDeep.currentStreakLabel')}</p>
                      <div className="w-16 h-16 mx-auto mb-2 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center">
                        <Flame className="h-8 w-8 text-white" />
                      </div>
                      <p className="text-lg font-bold text-gray-900" data-testid="streak-value">
                        {currentStreak}
                      </p>
                      <p className="text-xs text-gray-500">{currentStreak === 1 ? 'day' : 'days'}</p>
                      {streakStatus?.at_risk ? (
                        <p
                          className="text-[10px] text-amber-700 font-semibold mt-1 flex items-center justify-center gap-1"
                          data-testid="streak-at-risk"
                        >
                          <Shield className="h-3 w-3" /> At risk — finish today
                        </p>
                      ) : streakStatus?.grace_used ? (
                        <p
                          className="text-[10px] text-emerald-700 font-semibold mt-1 flex items-center justify-center gap-1"
                          data-testid="streak-protected"
                        >
                          <Shield className="h-3 w-3" /> {t('dashboardDeep.graceProtected')}
                        </p>
                      ) : (
                        <p className="text-xs text-orange-600 font-medium mt-1">
                          {currentStreak > 0 ? 'Keep it up 🔥' : 'Whenever you\'re ready'}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Today's Progress */}
                  <Card className="border-0 shadow-md" data-testid="dashboard-progress-card">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-600 mb-2">{t('dashboardDeep.todaysProgress')}</p>
                      <div className="relative w-16 h-16 mx-auto mb-2">
                        <svg className="transform -rotate-90 w-16 h-16">
                          <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="6" fill="transparent" />
                          <circle
                            cx="32" cy="32" r="28"
                            stroke="#a855f7"
                            strokeWidth="6"
                            fill="transparent"
                            strokeDasharray={`${(todaysProgress / 100) * 176} 176`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold text-gray-900" data-testid="progress-value">
                            {todaysProgress}%
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {tasksCompleted} of {totalTasks} actions
                      </p>
                    </CardContent>
                  </Card>

                </div>
              </div>

              {/* 4. AI INSIGHT CARD — the single, personalized daily insight */}
              {primaryInsight && (
                <Card
                  className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50 relative overflow-hidden"
                  data-testid="dashboard-ai-insight-card"
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-purple-600" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900">{t('dashboardDeep.insightForYou')}</h3>
                    </div>
                    <p className="text-base font-semibold text-gray-900 mb-2">
                      {primaryInsight.title}
                    </p>
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                      {primaryInsight.message}
                    </p>
                    {primaryInsight.action && (
                      <Button
                        onClick={() => navigate('/smart-check-in')}
                        className="bg-purple-600 hover:bg-purple-700 text-white h-10 px-4 rounded-xl text-sm font-semibold"
                        data-testid="dashboard-insight-action-btn"
                      >
                        {primaryInsight.action}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                    <div className="absolute right-0 bottom-0 w-24 h-24 opacity-20 pointer-events-none">
                      <InsightIcon className="h-24 w-24 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 5. TODAY'S HEALTH RECOMMENDATIONS — score-aware tiers */}
              {(() => {
                const score = healthScore;
                const tierFor = (task) => {
                  if (task.completed) return 'maintenance';
                  if (score < 50) return 'priority';
                  if (score < 70) return 'improvement';
                  return 'maintenance';
                };
                const sortedTasks = [...tasks].sort((a, b) => {
                  const ta = tierFor(a);
                  const tb = tierFor(b);
                  if (ta !== tb) return TIER_PRIORITY[ta] - TIER_PRIORITY[tb];
                  if (a.completed !== b.completed) return a.completed ? 1 : -1;
                  return FACTOR_ORDER.indexOf(a.id) - FACTOR_ORDER.indexOf(b.id);
                });
                const incompleteCount = sortedTasks.filter((t) => !t.completed).length;
                let visibleTasks = sortedTasks;
                if (score >= 70 && incompleteCount > 0) {
                  // Maintenance band — surface only the few items still pending
                  visibleTasks = sortedTasks.filter((t) => !t.completed).concat(
                    sortedTasks.filter((t) => t.completed),
                  );
                }
                const summaryLine =
                  score >= 85
                    ? t('dashboardDeep.recsSummary.excellent')
                    : score >= 70
                    ? t('dashboardDeep.recsSummary.good', { done: tasksCompleted, total: totalTasks })
                    : score >= 50
                    ? t('dashboardDeep.recsSummary.improvable', { done: tasksCompleted, total: totalTasks })
                    : t('dashboardDeep.recsSummary.needsAttention', { done: tasksCompleted, total: totalTasks });

                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('dashboardDeep.todaysHealthRecommendations')}
                      </h3>
                    </div>
                    <p
                      className="text-sm text-gray-600 mb-3"
                      data-testid="dashboard-recommendations-summary"
                    >
                      {summaryLine}
                    </p>
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4 space-y-2">
                        {visibleTasks.map((task, idx) => {
                          const Icon = TASK_ICONS[task.id] || Activity;
                          const isToggling = togglingTaskId === task.id;
                          const tier = tierFor(task);
                          const copy =
                            getRecommendationCopy(t, task.id, tier) || {
                              title: task.label,
                              description: '',
                            };
                          const points = task.points || 25;
                          const isFirstPriority =
                            tier === 'priority' &&
                            !task.completed &&
                            idx === 0;
                          return (
                            <button
                              key={task.id}
                              onClick={() => handleToggleTask(task.id)}
                              disabled={isToggling}
                              className="w-full flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-60 text-left"
                              data-testid={`dashboard-task-${task.id}-btn`}
                              data-tier={tier}
                            >
                              <div
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                                  task.completed
                                    ? 'bg-emerald-500 border-emerald-500'
                                    : 'border-gray-300'
                                }`}
                              >
                                {isToggling ? (
                                  <Loader2 className="h-3 w-3 text-emerald-600 animate-spin" />
                                ) : (
                                  task.completed && <Check className="h-4 w-4 text-white" />
                                )}
                              </div>
                              <div
                                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                  task.completed ? 'bg-emerald-100' : 'bg-gray-100'
                                }`}
                              >
                                <Icon
                                  className={`h-5 w-5 ${
                                    task.completed ? 'text-emerald-600' : 'text-gray-500'
                                  }`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p
                                    className={`text-sm font-semibold leading-snug ${
                                      task.completed ? 'text-gray-400 line-through' : 'text-gray-900'
                                    }`}
                                  >
                                    {copy.title}
                                  </p>
                                  {isFirstPriority && (
                                    <span
                                      className="text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700"
                                      data-testid={`dashboard-task-${task.id}-priority-badge`}
                                    >
                                      {t('dashboardDeep.priorityBadge')}
                                    </span>
                                  )}
                                </div>
                                {copy.description && (
                                  <p
                                    className={`text-xs leading-snug mt-0.5 ${
                                      task.completed ? 'text-gray-400' : 'text-gray-600'
                                    }`}
                                  >
                                    {copy.description}
                                  </p>
                                )}
                              </div>
                              <span
                                className={`text-xs font-bold tabular-nums shrink-0 mt-0.5 ${
                                  task.completed ? 'text-emerald-600' : 'text-emerald-700'
                                }`}
                              >
                                +{points} pts
                              </span>
                            </button>
                          );
                        })}
                      </CardContent>
                    </Card>
                    <button
                      className="w-full mt-3 text-sm text-emerald-600 font-semibold flex items-center justify-center gap-1"
                      onClick={() => navigate('/smart-check-in')}
                      data-testid="dashboard-view-all-tasks-btn"
                    >
                      {t('dashboardDeep.updateViaCheck')}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                );
              })()}

              {/* 5b. HEALTH INSIGHT BOX — personalised from incomplete factors */}
              <Card
                className="border border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-sm rounded-2xl"
                data-testid="dashboard-health-insight-box"
              >
                <CardContent className="p-5">
                  <p className="text-sm font-bold text-gray-900 mb-1.5 flex items-center gap-1.5">
                    <span className="text-base" aria-hidden="true">💡</span>
                    {t('dashboardDeep.healthInsight')}
                  </p>
                  <p
                    className="text-sm text-gray-700 leading-relaxed"
                    data-testid="dashboard-health-insight-text"
                  >
                    {buildHealthInsight(t, currentLanguage, tasks, healthScore)}
                  </p>
                </CardContent>
              </Card>

              {/* 5c. CHECK MY HEALTH RISK — primary CTA above Quick Access */}
              <button
                onClick={() => navigate('/health-risk-check')}
                className="w-full mt-2 bg-gradient-to-br from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600 text-white rounded-2xl px-6 py-5 shadow-lg flex items-center justify-between gap-4 transition-all"
                data-testid="dashboard-check-health-risk-btn"
              >
                <div className="flex items-center gap-4 text-left">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-base font-bold leading-tight">{t('dashboardDeep.checkMyHealthRisk')}</p>
                    <p className="text-xs text-rose-50/95 mt-1">
                      {t('dashboardDeep.riskCheckSubtext')}
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0" />
              </button>

              {/* 5d. CHILDREN'S HEALTH SNAPSHOT — surfaces if user has a child profile */}
              <ChildSnapshotCard />

              {/* 5e. EXPLORE CARE SERVICES — single clean CTA card.
                  The full clinical roadmap (specialist · pharmacy ·
                  wellness placeholders) lives on the dedicated
                  /care-services tab. Keeping just one CTA here avoids
                  long stacked placeholder scroll on the Dashboard. */}
              <button
                onClick={() => navigate('/care-services')}
                className="w-full text-left bg-white border border-gray-100 rounded-2xl px-5 py-4 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all flex items-center gap-4 group"
                data-testid="dashboard-explore-care-services-btn"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-sm">
                  <HeartHandshake className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900 leading-tight">
                      {t('dashboardDeep.exploreCareServicesTitle')}
                    </p>
                    <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0 border border-gray-200 font-medium">
                      {t('supportDeep.comingSoon')}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('dashboardDeep.exploreCareServicesSubtitle')}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-indigo-600 transition-colors shrink-0" />
              </button>

              {/* 6. QUICK ACCESS */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('dashboardDeep.quickAccess')}</h3>
                <div className="grid grid-cols-5 gap-4">
                  {/* Step Tracker — replaces the old Food & Diet shortcut.
                      Nutrition lives in the bottom-nav now, so this slot is
                      better used to surface daily activity tracking from the
                      Home screen. Subtle live indicator below shows today's
                      cached step count when > 0. */}
                  <button
                    onClick={() => navigate('/step-tracker')}
                    className="flex flex-col items-center gap-2"
                    data-testid="dashboard-quick-step-tracker-btn"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-md">
                      <Footprints className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center">{t('navShort.stepTracker', 'Step Tracker')}</span>
                    {/* Reserved sub-line — keeps every tile vertically
                        aligned even when a signal isn't available. */}
                    <span className="text-[10px] text-emerald-600 font-medium text-center h-3 leading-3" data-testid="dashboard-quick-step-subline">
                      {cachedSteps > 0 ? `${cachedSteps.toLocaleString()} today` : ''}
                    </span>
                  </button>

                  <button
                    onClick={() => navigate('/family-health')}
                    className="flex flex-col items-center gap-2"
                    data-testid="dashboard-quick-family-btn"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-md">
                      <Users className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center">{t('dashboardDeep.familyCare')}</span>
                    <span className="text-[10px] text-gray-400 font-medium text-center h-3 leading-3" aria-hidden="true">&nbsp;</span>
                  </button>

                  <button
                    onClick={() => navigate('/education')}
                    className="flex flex-col items-center gap-2"
                    data-testid="dashboard-quick-education-btn"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-md">
                      <BookOpen className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center">{t('dashboardDeep.healthLibrary')}</span>
                    <span className="text-[10px] text-gray-400 font-medium text-center h-3 leading-3" aria-hidden="true">&nbsp;</span>
                  </button>

                  {/* My Health Data — pairs naturally with the current
                      check-in streak we already display elsewhere on Home. */}
                  <button
                    onClick={() => navigate('/preventive-health')}
                    className="flex flex-col items-center gap-2"
                    data-testid="dashboard-quick-metrics-btn"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-red-500 rounded-2xl flex items-center justify-center shadow-md">
                      <TrendingUp className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center">{t('dashboardDeep.myHealthData')}</span>
                    <span className="text-[10px] text-pink-600 font-medium text-center h-3 leading-3" data-testid="dashboard-quick-metrics-subline">
                      {currentStreak > 0 ? `${currentStreak}d streak` : ''}
                    </span>
                  </button>

                  <button
                    onClick={() => navigate('/support-help#faq')}
                    className="flex flex-col items-center gap-2"
                    data-testid="dashboard-quick-support-btn"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-md">
                      <HelpCircle className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center">{t('dashboardDeep.askQuestion')}</span>
                    <span className="text-[10px] text-gray-400 font-medium text-center h-3 leading-3" aria-hidden="true">&nbsp;</span>
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>

      {milestoneStreak && (
        <MilestoneModal
          streak={milestoneStreak}
          onClose={() => setMilestoneStreak(null)}
        />
      )}

      {/* Global Search modal lives at the App root via GlobalSearchProvider;
          this dashboard only needs to surface triggers (header icon + hero
          pill) that call `openSearch()` from context. */}
    </div>
  );
};

export default Dashboard;
